import { useState, useEffect } from 'react';
import { LockClosedIcon } from '@heroicons/react/24/solid';
import { useTrialStore } from '../store/trialStore';
import api from '../utils/api';

/**
 * Hero shown atop dashboard when lockState === 'view_only'.
 * Displays personalized stats (videos/collections/AI count) + recovery CTA.
 *
 * Trial users haven't chosen a paid tier yet, so the CTA opens the plan picker
 * (RecoverySubscriptionModal) instead of one-click-paying a hardcoded plan/price.
 * The stepped recovery discount (recoveryDiscountPercent) is server-sourced and
 * shown inside the picker.
 *
 * Stats sourced from GET /api/dashboard/stats which returns
 *   { overview: { totalCollections, totalActiveAccounts, totalContent, last7DaysContent } }
 * AI-insights count not exposed there yet — we fall back to 0 until backend ships it.
 */
const ViewOnlyLockHero = () => {
  const { lockState, recoveryDiscountPercent } = useTrialStore();
  const [stats, setStats] = useState({ videos: 0, collections: 0, aiInsights: 0 });

  useEffect(() => {
    if (lockState !== 'view_only') return;
    api.get('/api/dashboard/stats')
      .then(res => {
        const overview = res.data?.data?.overview || {};
        setStats({
          videos: overview.totalContent || 0,
          collections: overview.totalCollections || 0,
          aiInsights: overview.totalAiInsights || 0,
        });
      })
      .catch(() => {});
  }, [lockState]);

  if (lockState !== 'view_only') return null;

  // Open the shared plan picker (RecoverySubscriptionModal listens for this event).
  const openPicker = () => window.dispatchEvent(new Event('account-locked'));

  const hasData = stats.videos > 0 || stats.collections > 0 || stats.aiInsights > 0;

  return (
    <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 w-[min(94vw,560px)]">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 bg-zinc-900/95 backdrop-blur border border-purple-500/40 ring-1 ring-lime-400/10 rounded-2xl shadow-2xl shadow-purple-900/30 px-5 py-4">
        {/* Lock badge + pitch */}
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-purple-500/15 border border-purple-500/30 flex items-center justify-center">
            <LockClosedIcon className="w-5 h-5 text-purple-300" />
          </div>
          <div className="min-w-0">
            <div className="text-white font-bold text-[15px] leading-tight">Твой Pro закончился</div>
            <div className="text-zinc-400 text-xs mt-0.5 truncate">
              {hasData ? (
                <>Все данные на месте: <span className="text-zinc-200">{stats.videos} видео · {stats.collections} коллекций</span></>
              ) : (
                'Все данные сохранены — верни доступ'
              )}
            </div>
          </div>
        </div>
        {/* CTA */}
        <button
          onClick={openPicker}
          className="flex-shrink-0 w-full sm:w-auto bg-lime-400 hover:bg-lime-300 text-zinc-900 font-bold text-sm px-4 py-2.5 rounded-xl whitespace-nowrap transition-colors shadow-lg shadow-lime-400/20"
        >
          {recoveryDiscountPercent > 0 ? `Вернуть Pro · −${recoveryDiscountPercent}%` : 'Вернуть Pro'}
        </button>
      </div>
    </div>
  );
};

export default ViewOnlyLockHero;
